import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Send, Plus, Trash2, Loader2, BarChart3, MessageSquare, ImagePlus, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import KPIDashboard from "./KPIDashboard";

const SUGGESTED_PROMPTS = [
  "Dame un análisis ejecutivo de la situación financiera hoy y las 3 acciones más urgentes.",
  "¿Tengo excedente de tesorería? Si sí, ¿cuánto puedo mover sin poner en riesgo la operativa a 6 meses?",
  "Analiza mis top 15 proveedores por gasto. ¿Con cuáles podría renegociar y por qué?",
  "Revisa los gastos fijos (nóminas, estructura, impuestos, préstamos). ¿Dónde hay margen de optimización?",
  "Muéstrame los proyectos con peor margen. ¿Qué patrones ves? ¿Qué debería revisar del pricing?",
  "Si tuviera 100.000€ excedentes hoy, ¿qué horizonte y perfil de inversión propondrías?",
];

export default function KPIs() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [attachments, setAttachments] = useState([]); // [{name, dataUrl}]
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const isOwner = (user?.email || "").toLowerCase() === "eduardo@grabarte360.com";

  const loadSessions = () => {
    api.get("/kpis/sessions").then(r => setSessions(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (isOwner && tab === "chat") loadSessions();
  }, [isOwner, tab]);

  useEffect(() => {
    if (!activeSessionId) { setMessages([]); return; }
    api.get(`/kpis/messages/${activeSessionId}`).then(r => setMessages(r.data)).catch(() => {});
  }, [activeSessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  if (!isOwner) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white border border-[var(--border)] rounded-lg p-8 text-center">
          <Sparkles className="mx-auto mb-3 text-[var(--ink-muted)]" size={32} />
          <h1 className="font-display text-2xl font-black mb-2">Módulo restringido</h1>
          <p className="text-sm text-[var(--ink-muted)]">Esta sección es exclusiva del propietario.</p>
        </div>
      </div>
    );
  }

  const newChat = () => {
    const id = crypto.randomUUID();
    setActiveSessionId(id);
    setMessages([]);
    setStreamingText("");
  };

  const send = async (textOverride) => {
    const text = (textOverride || input).trim();
    const hasImages = attachments.length > 0;
    if ((!text && !hasImages) || sending) return;
    let sid = activeSessionId;
    if (!sid) {
      sid = crypto.randomUUID();
      setActiveSessionId(sid);
    }
    const currentAttachments = attachments;
    setInput("");
    setAttachments([]);
    setSending(true);
    setStreamingText("");
    // Optimistic user msg (con marca de imágenes adjuntas)
    const suffix = hasImages ? `\n\n[+${currentAttachments.length} imagen${currentAttachments.length!==1?"es":""} adjunta${currentAttachments.length!==1?"s":""}]` : "";
    const userMsg = { role: "user", content: text + suffix, id: `u-${Date.now()}`, attachments: currentAttachments };
    setMessages(prev => [...prev, userMsg]);

    try {
      const r = await api.post("/kpis/chat", {
        session_id: sid,
        message: text || (hasImages ? "Analiza esta imagen." : ""),
        year: new Date().getFullYear(),
        images_base64: currentAttachments.map(a => a.dataUrl),
      }, { timeout: 180000 });
      const assistantText = r.data?.content || "(sin respuesta)";
      setMessages(prev => [...prev, { role: "assistant", content: assistantText, id: `a-${Date.now()}` }]);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Error al llamar a la IA");
    } finally {
      setSending(false);
    }
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) return;
    const toBase64 = (f) => new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve({ name: f.name || "captura.png", dataUrl: r.result });
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    try {
      const results = await Promise.all(files.map(toBase64));
      // Filtrar imágenes >8MB
      const filtered = results.filter(r => (r.dataUrl?.length || 0) < 8 * 1024 * 1024 * 1.4);
      if (filtered.length < results.length) toast.warning("Alguna imagen era demasiado grande y se ignoró");
      setAttachments(prev => [...prev, ...filtered].slice(0, 5));
      if (results.length && filtered.length) toast.success(`${filtered.length} imagen${filtered.length!==1?"es":""} adjunta${filtered.length!==1?"s":""}`);
    } catch (e) { toast.error("Error leyendo la imagen"); }
  };

  const onPaste = async (e) => {
    const items = e.clipboardData?.items || [];
    const imgs = [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) imgs.push(f);
      }
    }
    if (imgs.length > 0) {
      e.preventDefault();
      await addFiles(imgs);
    }
    // Si no hay imágenes, se pega el texto normalmente (comportamiento por defecto)
  };

  const deleteSession = async (id) => {
    if (!window.confirm("¿Borrar esta conversación?")) return;
    await api.delete(`/kpis/sessions/${id}`);
    if (activeSessionId === id) { setActiveSessionId(null); setMessages([]); }
    loadSessions();
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="kpis-page">
      {/* Top tabs */}
      <div className="border-b border-[var(--border)] bg-white px-6 flex gap-1">
        {[
          { v: "dashboard", l: "Dashboard KPIs", icon: BarChart3 },
          { v: "chat", l: "Análisis IA (Claude)", icon: MessageSquare },
        ].map(o => {
          const Icon = o.icon;
          const active = tab === o.v;
          return (
            <button
              key={o.v}
              data-testid={`kpi-tab-${o.v}`}
              onClick={() => setTab(o.v)}
              className={`px-4 py-3 text-sm inline-flex items-center gap-2 border-b-2 -mb-px ${active ? "border-black text-[var(--ink)] font-medium" : "border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]"}`}
            >
              <Icon size={15}/> {o.l}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" ? (
        <div className="flex-1 overflow-y-auto">
          <KPIDashboard />
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
      {/* Sidebar sessions */}
      <aside className="w-72 border-r border-[var(--border)] bg-[var(--bg)] flex flex-col">
        <div className="p-3 border-b border-[var(--border)]">
          <button
            data-testid="kpi-new-chat"
            onClick={newChat}
            className="w-full inline-flex items-center justify-center gap-2 bg-black text-white text-sm px-4 py-2 rounded hover:opacity-90"
          >
            <Plus size={14} /> Nuevo análisis
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <div className="p-3 text-xs text-[var(--ink-muted)]">Sin conversaciones aún.</div>
          )}
          {sessions.map(s => (
            <div
              key={s.session_id}
              onClick={() => setActiveSessionId(s.session_id)}
              className={`group flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm ${activeSessionId === s.session_id ? "bg-white border border-[var(--border)]" : "hover:bg-white"}`}
            >
              <div className="flex-1 truncate">{s.title || "Análisis"}</div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                className="opacity-0 group-hover:opacity-100 text-[var(--ink-muted)] hover:text-rose-600"
                title="Borrar"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col">
        <header className="border-b border-[var(--border)] px-6 py-3 flex items-center gap-3 bg-white">
          <Sparkles size={18} />
          <div>
            <div className="font-display text-lg font-black leading-tight">KPIs · Análisis Financiero con IA</div>
            <div className="text-xs text-[var(--ink-muted)]">Claude Sonnet 4.5 · con acceso a tu cashflow, facturas, bancos, proyectos y gastos</div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {messages.length === 0 && !streamingText && (
            <div className="max-w-3xl mx-auto">
              <p className="text-sm text-[var(--ink-muted)] mb-4">Empieza con una de estas preguntas o escribe la tuya:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SUGGESTED_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    data-testid={`kpi-suggested-${i}`}
                    onClick={() => send(p)}
                    disabled={sending}
                    className="text-left text-sm p-3 border border-[var(--border)] rounded-lg bg-white hover:bg-[var(--hover)] disabled:opacity-50"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((m) => (
              <MessageBlock key={m.id || m.created_at} role={m.role} content={m.content} attachments={m.attachments}/>
            ))}
            {streamingText && (
              <MessageBlock role="assistant" content={streamingText} streaming />
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <footer className="border-t border-[var(--border)] p-4 bg-white">
          <div className="max-w-3xl mx-auto">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="kpi-attachments">
                {attachments.map((a, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden border border-[var(--border)] bg-[var(--bg)]">
                    <img src={a.dataUrl} alt={a.name} className="w-full h-full object-cover"/>
                    <button
                      data-testid={`kpi-remove-attachment-${i}`}
                      onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                      className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full p-0.5 hover:bg-black"
                    >
                      <XIcon size={11}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-end">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => { addFiles(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}
                className="hidden"
              />
              <button
                data-testid="kpi-attach-image"
                onClick={() => fileRef.current?.click()}
                disabled={sending}
                title="Adjuntar imagen (o pega desde el portapapeles)"
                className="p-3 border border-[var(--border)] rounded-md hover:bg-[var(--hover)] disabled:opacity-30"
              >
                <ImagePlus size={16}/>
              </button>
              <textarea
                data-testid="kpi-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPaste={onPaste}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Pregunta sobre tu cashflow, gastos, inversión… (puedes pegar imágenes desde el portapapeles)"
                rows={2}
                disabled={sending}
                className="flex-1 border border-[var(--border)] rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:border-black disabled:opacity-50"
              />
              <button
                data-testid="kpi-send"
                onClick={() => send()}
                disabled={(!input.trim() && attachments.length === 0) || sending}
                className="bg-black text-white p-3 rounded-md disabled:opacity-30 hover:opacity-90"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
          <p className="text-[10px] text-[var(--ink-muted)] text-center mt-2 max-w-3xl mx-auto">
            La IA genera análisis basados en tus datos. No constituye asesoramiento financiero profesional.
          </p>
        </footer>
      </main>
        </div>
      )}
    </div>
  );
}

function MessageBlock({ role, content, streaming, attachments }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-lg px-4 py-3 ${isUser ? "bg-black text-white" : "bg-white border border-[var(--border)]"}`}>
        {attachments?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a, i) => (
              <img key={i} src={a.dataUrl} alt={a.name} className="max-h-40 rounded border border-white/20"/>
            ))}
          </div>
        )}
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap">{content}</p>
        ) : (
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            {streaming && <span className="inline-block w-2 h-4 bg-black animate-pulse ml-1" />}
          </div>
        )}
      </div>
    </div>
  );
}
